import React, { useEffect, useRef, useState } from 'react';
import { searchDoctors } from '../../services/npiService';
import { inp, lbl, noAutofill, row2 } from './shared';

const PROVIDER_OPTIONS = [
  'Aetna', 'Anthem', 'Blue Cross Blue Shield', 'Bright Health',
  'CareFirst BlueCross BlueShield', 'Centene', 'Cigna',
  'CVS Health / Caremark', 'Florida Blue', 'Harvard Pilgrim Health Care',
  'Health Net', 'HealthPartners', 'Highmark', 'Humana',
  'Independence Blue Cross', 'Kaiser Permanente', 'Medica',
  'Molina Healthcare', 'Oscar Health', 'Premera Blue Cross',
  'Priority Health', 'Providence Health Plan',
  'Regence BlueCross BlueShield', 'Tufts Health Plan',
  'UPMC Health Plan', 'UnitedHealthcare', 'WellCare',
].sort();

function ProviderTypeahead({ value, onChange }) {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    function close(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const matches = query.length >= 1
    ? PROVIDER_OPTIONS.filter(provider => provider.toLowerCase().includes(query.toLowerCase()))
    : PROVIDER_OPTIONS;

  function select(provider) {
    setQuery(provider);
    onChange(provider);
    setOpen(false);
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        style={inp}
        value={query}
        placeholder="Type to search or enter any name..."
        {...noAutofill('provider-search')}
        onChange={e => {
          setQuery(e.target.value);
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {open && matches.length > 0 && (
        <div style={dropdownStyle}>
          {matches.map(provider => (
            <div
              key={provider}
              onMouseDown={() => select(provider)}
              style={optionStyle}
              onMouseEnter={e => e.currentTarget.style.background = '#dbeef8'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              {provider}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Providers({ data, onChange }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [outOfState, setOutOfState] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [manualMode, setManualMode] = useState(false);
  const [manualName, setManualName] = useState('');
  const [manualSpecialty, setManualSpecialty] = useState('');
  const [manualCity, setManualCity] = useState('');
  const [manualState, setManualState] = useState('');
  const firstNameRef = useRef(null);

  const preferredProviders = data.preferredProviders || [''];
  const doctors = data.doctors || [];
  const clientState = data.state || '';

  function updateProvider(index, value) {
    const next = [...preferredProviders];
    next[index] = value;
    onChange('preferredProviders', next);
  }

  async function handleSearch(event) {
    event.preventDefault();
    if (!lastName.trim()) return;
    setLoading(true);
    setSearched(false);
    setResults([]);
    setSearchError('');
    try {
      const state = outOfState ? '' : clientState;
      const response = await searchDoctors(firstName, lastName, state);
      setResults(response);
      setSearched(true);
    } catch (err) {
      setSearchError(err.message || 'Search failed.');
      setResults([]);
      setSearched(true);
    }
    setLoading(false);
  }

  function selectDoctor(doctor) {
    onChange('doctors', [...doctors, doctor]);
    setFirstName('');
    setLastName('');
    setResults([]);
    setSearched(false);
    setTimeout(() => firstNameRef.current?.focus(), 50);
  }

  function removeDoctor(index) {
    onChange('doctors', doctors.filter((_, i) => i !== index));
  }

  function addManualDoctor(event) {
    event.preventDefault();
    if (!manualName.trim()) return;
    onChange('doctors', [...doctors, {
      npi: '',
      name: manualName.trim(),
      credential: '',
      specialty: manualSpecialty.trim(),
      city: manualCity.trim(),
      state: manualState.trim(),
    }]);
    setManualName('');
    setManualSpecialty('');
    setManualCity('');
    setManualState('');
    setManualMode(false);
  }

  return (
    <div>
      <div style={sectionDivider}>
        <div style={sectionHeading}>Preferred Providers</div>
        <p style={helpText}>
          Insurance companies or health networks you prefer.
        </p>
        {preferredProviders.map((value, index) => (
          <div key={index} style={lineRow}>
            <div style={{ flex: 1 }}>
              <ProviderTypeahead value={value} onChange={next => updateProvider(index, next)} />
            </div>
            {preferredProviders.length > 1 && (
              <button type="button" style={removeBtn} onClick={() => onChange('preferredProviders', preferredProviders.filter((_, i) => i !== index))}>
                ×
              </button>
            )}
          </div>
        ))}
        <button type="button" style={addBtn} onClick={() => onChange('preferredProviders', [...preferredProviders, ''])}>
          + Add Provider
        </button>
      </div>

      <div style={sectionDivider}>
        <div style={sectionHeading}>Doctors</div>
        <div style={instructionBox}>
          <strong style={{ color: '#1a3364' }}>Search doctors by name:</strong>
          <ol style={{ margin: '6px 0 0', paddingLeft: 20 }}>
            <li>Enter a last name and optional first name.</li>
            <li>Click Search, then select the correct doctor from the results.</li>
          </ol>
        </div>

        <form onSubmit={handleSearch} style={searchBox}>
          <div style={row2}>
            <div>
              <label style={lbl}>First Name <span style={optional}>(optional)</span></label>
              <input ref={firstNameRef} style={inp} value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="e.g. Jane" {...noAutofill('doctor-first-name')} />
            </div>
            <div>
              <label style={lbl}>Last Name <span style={optional}>(required)</span></label>
              <input style={inp} value={lastName} onChange={e => setLastName(e.target.value)} placeholder="e.g. Smith" {...noAutofill('doctor-last-name')} />
            </div>
          </div>

          {clientState && (
            <label style={stateCheck}>
              <input type="checkbox" checked={outOfState} onChange={e => setOutOfState(e.target.checked)} style={{ width: 16, height: 16, accentColor: '#1a3364' }} />
              Search outside {clientState}
            </label>
          )}

          <button type="submit" disabled={!lastName.trim() || loading} style={{
            ...searchBtn,
            background: lastName.trim() && !loading ? '#2a5298' : '#b8cfe8',
            cursor: lastName.trim() && !loading ? 'pointer' : 'default',
          }}>
            {loading ? 'Searching...' : 'Search'}
          </button>

          {searchError && <div style={errorBox}>{searchError}</div>}
          {searched && results.length === 0 && !loading && !searchError && (
            <div style={emptyText}>No results found. Try last name only or search outside the state.</div>
          )}
          <label style={stateCheck}>
            <input type="checkbox" checked={manualMode} onChange={e => setManualMode(e.target.checked)} style={{ width: 16, height: 16, accentColor: '#1a3364' }} />
            Can't find your doctor? Enter their info manually
          </label>
          {manualMode && (
            <div style={manualBox}>
              <div style={{ marginBottom: 10 }}>
                <label style={lbl}>Doctor Name <span style={optional}>(required)</span></label>
                <input style={inp} value={manualName} onChange={e => setManualName(e.target.value)} placeholder="e.g. Dr. Jane Smith" {...noAutofill('manual-doctor-name')} />
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={lbl}>Specialty <span style={optional}>(optional)</span></label>
                <input style={inp} value={manualSpecialty} onChange={e => setManualSpecialty(e.target.value)} placeholder="e.g. Cardiology" {...noAutofill('manual-doctor-specialty')} />
              </div>
              <div style={{ ...row2, marginBottom: 12 }}>
                <div>
                  <label style={lbl}>City <span style={optional}>(optional)</span></label>
                  <input style={inp} value={manualCity} onChange={e => setManualCity(e.target.value)} {...noAutofill('manual-doctor-city')} />
                </div>
                <div>
                  <label style={lbl}>State <span style={optional}>(optional)</span></label>
                  <input style={inp} value={manualState} onChange={e => setManualState(e.target.value)} placeholder="e.g. CO" maxLength={2} {...noAutofill('manual-doctor-state')} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={addManualDoctor} disabled={!manualName.trim()} style={{
                  ...searchBtn,
                  background: manualName.trim() ? '#2a5298' : '#b8cfe8',
                  cursor: manualName.trim() ? 'pointer' : 'default',
                }}>
                  + Add Doctor
                </button>
                <button type="button" onClick={() => setManualMode(false)} style={cancelBtn}>
                  Cancel
                </button>
              </div>
            </div>
          )}
          {results.length === 200 && (
            <div style={capHint}>
              Showing the first 200 results. Add a first name or check the out-of-state box differently to narrow your search.
            </div>
          )}
          {results.length > 0 && (
            <div style={resultsBox}>
              {results.map((doctor, index) => (
                <div
                  key={index}
                  onClick={() => selectDoctor(doctor)}
                  style={resultItem}
                  onMouseEnter={e => e.currentTarget.style.background = '#dbeef8'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                >
                  <div style={{ fontWeight: 700, color: '#1a3364', fontSize: 14 }}>
                    {doctor.name}{doctor.credential ? `, ${doctor.credential}` : ''}
                  </div>
                  <div style={{ fontSize: 12, color: '#4a6288', marginTop: 2 }}>
                    {[doctor.specialty, doctor.city, doctor.state].filter(Boolean).join(' - ')}
                  </div>
                  {doctor.npi && <div style={{ fontSize: 11, color: '#7a99b8', marginTop: 1 }}>NPI: {doctor.npi}</div>}
                </div>
              ))}
            </div>
          )}
        </form>

        {doctors.length > 0 && (
          <div>
            <div style={addedHeading}>Added Doctors <span style={countPill}>{doctors.length}</span></div>
            {doctors.map((doctor, index) => (
              <div key={index} style={addedDoctor}>
                <div>
                  <div style={{ fontWeight: 700, color: '#1a3364', fontSize: 14 }}>
                    {doctor.name}{doctor.credential ? `, ${doctor.credential}` : ''}
                  </div>
                  <div style={{ fontSize: 13, color: '#4a6288', marginTop: 2 }}>
                    {[doctor.specialty, doctor.city, doctor.state].filter(Boolean).join(' - ')}
                  </div>
                  {doctor.npi && <div style={{ fontSize: 12, color: '#7a99b8', marginTop: 1 }}>NPI: {doctor.npi}</div>}
                </div>
                <button type="button" style={removeBtn} onClick={() => removeDoctor(index)}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const dropdownStyle = { position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1.5px solid #c5d5e8', borderRadius: 8, zIndex: 200, maxHeight: 220, overflowY: 'auto', boxShadow: '0 6px 16px rgba(26,51,100,0.13)', marginTop: 2 };
const optionStyle = { padding: '9px 14px', cursor: 'pointer', fontSize: 14, color: '#1a3364', background: '#fff' };
const sectionDivider = { borderTop: '1.5px solid #dbeef8', paddingTop: 20, marginBottom: 22 };
const sectionHeading = { fontSize: 13, fontWeight: 700, color: '#1a3364', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 14 };
const helpText = { fontSize: 13, color: '#4a6288', marginTop: 0, marginBottom: 14 };
const lineRow = { display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' };
const addBtn = { padding: '9px 20px', border: '2px dashed #2a5298', borderRadius: 8, background: '#f4f8fc', color: '#2a5298', fontWeight: 600, fontSize: 14, cursor: 'pointer', marginTop: 2 };
const removeBtn = { padding: '9px 12px', border: 'none', background: '#f5e8e8', borderRadius: 8, cursor: 'pointer', color: '#b03030', fontWeight: 700, fontSize: 18, lineHeight: 1, flexShrink: 0 };
const instructionBox = { background: '#f4f8fc', border: '1.5px solid #dbeef8', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#4a6288', lineHeight: 1.6 };
const searchBox = { background: '#f4f8fc', border: '1.5px solid #dbeef8', borderRadius: 10, padding: '18px 18px 14px', marginBottom: 20 };
const optional = { fontWeight: 400, textTransform: 'none', letterSpacing: 0 };
const stateCheck = { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: 13, color: '#4a6288', cursor: 'pointer' };
const searchBtn = { padding: '9px 22px', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 600, fontSize: 14, marginBottom: 12 };
const errorBox = { fontSize: 13, color: '#c0392b', background: '#fdf0ef', border: '1px solid #f5c6c2', borderRadius: 6, padding: '8px 12px', marginTop: 8 };
const capHint = { fontSize: 13, color: '#c0860a', background: '#fdf6e8', border: '1px solid #f0deb0', borderRadius: 6, padding: '8px 12px', marginTop: 8, marginBottom: 8 };
const emptyText = { fontSize: 13, color: '#7a99b8', marginTop: 8 };
const resultsBox = { background: '#fff', border: '1.5px solid #c5d5e8', borderRadius: 8, maxHeight: 280, overflowY: 'auto', boxShadow: '0 4px 12px rgba(26,51,100,0.10)' };
const resultItem = { padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f4f8fc' };
const addedHeading = { fontSize: 13, fontWeight: 700, color: '#1a3364', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 };
const countPill = { background: '#1a3364', color: '#fff', borderRadius: '999px', padding: '1px 8px', fontSize: 12, fontWeight: 700, letterSpacing: 0, textTransform: 'none' };
const addedDoctor = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', border: '1.5px solid #dbeef8', borderRadius: 8, padding: '11px 14px', marginBottom: 8 };
const manualBox = { background: '#fff', border: '1.5px solid #c5d5e8', borderRadius: 8, padding: 14, marginTop: 10, marginBottom: 10 };
const cancelBtn = { padding: '9px 16px', border: '1.5px solid #c5d5e8', borderRadius: 8, background: '#fff', color: '#4a6288', fontWeight: 600, fontSize: 14, cursor: 'pointer' };
